import axios from 'axios';
import { Config } from '../config/config';

export interface License {
  id: string;
  displayName: string;
  email: string;
  subscribedUntil: string | null;
  licenseType: string;
  licenseStatus: string;
  licenseKey: string;
  daysRemaining: number;
  isActive: boolean;
  features: {
    aiDetection: boolean;
    remoteDiagnostics: boolean;
    multipleDevices: boolean;
    advancedReporting: boolean;
    prioritySupport: boolean;
  };
}

export interface LicenseDetail extends License {
  history: Array<{
    type: string;
    startDate: string;
    endDate: string;
    transactionId: string;
  }>;
}

export interface LicenseUpdateRequest {
  licenseType?: string;
  subscribedUntil?: string;
  licenseStatus?: string;
  licenseFeaturesEnabled?: {
    aiDetection?: boolean;
    remoteDiagnostics?: boolean;
    multipleDevices?: boolean;
    advancedReporting?: boolean;
    prioritySupport?: boolean;
  };
}

export interface LicenseGenerateRequest {
  accountId: string;
  licenseType: string;
  durationMonths?: number;
  features?: {
    multipleDevices?: boolean;
    advancedReporting?: boolean;
    prioritySupport?: boolean;
  };
}

class LicenseService {
  private apiUrl = Config.apiRoot.endsWith('/api') 
    ? `${Config.apiRoot}/licenses` 
    : `${Config.apiRoot}/api/licenses`;

  /**
   * Alle Lizenzen abrufen
   */
  async getAllLicenses(): Promise<License[]> {
    try {
      const response = await axios.get<License[]>(this.apiUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Fehler beim Abrufen der Lizenzen:', error);
      throw error;
    }
  }

  /**
   * Details einer einzelnen Lizenz abrufen
   */
  async getLicenseDetails(id: string): Promise<LicenseDetail> {
    try {
      const response = await axios.get<LicenseDetail>(`${this.apiUrl}/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Fehler beim Abrufen der Lizenzdetails für ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Eine Lizenz aktualisieren
   */
  async updateLicense(id: string, data: LicenseUpdateRequest): Promise<any> {
    try {
      const response = await axios.put(`${this.apiUrl}/${id}`, data, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Fehler beim Aktualisieren der Lizenz mit ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Eine neue Lizenz generieren
   */
  async generateLicense(data: LicenseGenerateRequest): Promise<any> {
    try {
      const response = await axios.post(`${this.apiUrl}/generate`, data, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Fehler beim Generieren einer neuen Lizenz:', error);
      throw error;
    }
  }

  /**
   * Eine Lizenz widerrufen
   */
  async revokeLicense(id: string): Promise<any> {
    try {
      const response = await axios.delete(`${this.apiUrl}/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Fehler beim Widerrufen der Lizenz mit ID ${id}:`, error);
      throw error;
    }
  }
}

export const licenseService = new LicenseService(); 